import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaEdit, FaPlus, FaRegSave, FaSync, FaTimes, FaTrash, FaWarehouse } from 'react-icons/fa';
import './FtzSiteConfiguration.css';

const API = import.meta.env.VITE_API_URL;

const emptyForm = {
  id: 0,
  warehouseid: '',
  zone_number: '',
  site_number: '',
  port_code: '',
  firms_code: '',
  operator_name: '',
  operator_number: '',
  direct_delivery_enabled: 0,
  active: 1,
};

async function fetchJSON(url, opts = {}) {
  const res = await fetch(url, { credentials: 'include', ...opts });
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.error) message = body.error;
    } catch {}
    throw new Error(message);
  }
  return res.json();
}

function normalizeSite(site = {}) {
  return {
    ...emptyForm,
    ...site,
    id: Number(site.id || 0) || 0,
    warehouseid: site.warehouseid == null ? '' : Number(site.warehouseid),
    zone_number: site.zone_number == null ? '' : String(site.zone_number),
    site_number: site.site_number == null ? '' : String(site.site_number),
    port_code: site.port_code == null ? '' : String(site.port_code),
    firms_code: site.firms_code == null ? '' : String(site.firms_code),
    operator_name: site.operator_name == null ? '' : String(site.operator_name),
    operator_number: site.operator_number == null ? '' : String(site.operator_number),
    direct_delivery_enabled: Number(site.direct_delivery_enabled || 0) === 1 ? 1 : 0,
    active: Number(site.active ?? 1) === 1 ? 1 : 0,
  };
}

export default function FtzSiteConfiguration() {
  const [sites, setSites] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedId, setSelectedId] = useState(0);
  const [previousSelectedId, setPreviousSelectedId] = useState(0);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const editorRef = useRef(null);
  const warehouseSelectRef = useRef(null);
  const zoneInputRef = useRef(null);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('topbar:setTitle', {
      detail: { title: 'FTZ Site Configuration', subtitle: '' },
    }));
    return () => window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
  }, []);

  const loadAll = async () => {
    setLoading(true);
    setStatus('');
    try {
      const [siteRows, warehouseRows] = await Promise.all([
        fetchJSON(`${API}/api/ftz/sites?includeInactive=${showInactive ? '1' : '0'}`),
        fetchJSON(`${API}/api/warehouses`),
      ]);
      const nextSites = Array.isArray(siteRows) ? siteRows.map(normalizeSite) : [];
      const nextWarehouses = Array.isArray(warehouseRows) ? warehouseRows : [];
      setSites(nextSites);
      setWarehouses(nextWarehouses);

      if (selectedId) {
        const current = nextSites.find((row) => Number(row.id) === Number(selectedId));
        if (current) {
          setIsCreatingNew(false);
          setIsEditing(false);
          setForm({ ...current });
        } else {
          setSelectedId(0);
          setIsCreatingNew(false);
          setIsEditing(false);
          setForm({ ...emptyForm });
        }
      }
    } catch (error) {
      setStatus(error.message || 'Failed to load FTZ sites');
      setSites([]);
      setWarehouses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive]);

  const warehouseNameById = useMemo(() => {
    const map = new Map();
    for (const wh of warehouses) map.set(Number(wh.id), wh.name || `Warehouse ${wh.id}`);
    return map;
  }, [warehouses]);

  const selectSite = (site) => {
    setSelectedId(Number(site?.id || 0) || 0);
    setIsCreatingNew(false);
    setIsEditing(false);
    setForm({ ...emptyForm, ...normalizeSite(site) });
    setStatus('');
  };

  const startNew = () => {
    setPreviousSelectedId(Number(selectedId || 0) || 0);
    setSelectedId(0);
    setIsCreatingNew(true);
    setIsEditing(true);
    setForm((current) => ({
      ...emptyForm,
      warehouseid: warehouses.length === 1
        ? Number(warehouses[0]?.id || 0) || ''
        : '',
      active: 1,
      direct_delivery_enabled: 0,
      // If the user was editing a site, keep the last warehouse as a convenience.
      ...(current?.warehouseid && warehouses.length > 1 ? { warehouseid: current.warehouseid } : {}),
    }));
    setStatus('Creating new FTZ site');
    window.requestAnimationFrame(() => {
      editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (warehouseSelectRef.current && !warehouseSelectRef.current.value) {
        warehouseSelectRef.current.focus();
      } else {
        zoneInputRef.current?.focus();
      }
    });
  };

  const cancelNew = () => {
    const priorId = Number(previousSelectedId || 0) || 0;
    if (priorId) {
      const priorSite = sites.find((site) => Number(site.id) === priorId);
      if (priorSite) {
        selectSite(priorSite);
        return;
      }
    }
    setSelectedId(0);
    setIsCreatingNew(false);
    setIsEditing(false);
    setForm({ ...emptyForm });
    setStatus('');
  };

  const startEdit = () => {
    if (!form.id) return;
    setIsEditing(true);
    setStatus('');
    window.requestAnimationFrame(() => {
      zoneInputRef.current?.focus();
    });
  };

  const cancelEdit = () => {
    if (!form.id) {
      cancelNew();
      return;
    }
    const current = sites.find((site) => Number(site.id) === Number(form.id));
    setForm(current ? normalizeSite(current) : { ...emptyForm });
    setIsEditing(false);
    setStatus('');
  };

  const saveSite = async () => {
    const payload = {
      warehouseid: form.warehouseid === '' ? null : Number(form.warehouseid),
      zone_number: form.zone_number,
      site_number: form.site_number,
      port_code: form.port_code,
      firms_code: form.firms_code,
      operator_name: form.operator_name,
      operator_number: form.operator_number,
      direct_delivery_enabled: Number(form.direct_delivery_enabled || 0),
      active: Number(form.active || 0),
    };
    setSaving(true);
    setStatus('');
    try {
      const saved = form.id
        ? await fetchJSON(`${API}/api/ftz/sites/${form.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          })
        : await fetchJSON(`${API}/api/ftz/sites`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
      const normalized = normalizeSite(saved || {});
      setSelectedId(normalized.id || 0);
      setIsCreatingNew(false);
      setIsEditing(false);
      setForm(normalized);
      await loadAll();
      setStatus('FTZ site saved');
    } catch (error) {
      setStatus(error.message || 'Failed to save FTZ site');
    } finally {
      setSaving(false);
    }
  };

  const deactivateSite = async () => {
    if (!form.id) return;
    if (!window.confirm('Deactivate this FTZ site?')) return;
    setSaving(true);
    setStatus('');
    try {
      await fetchJSON(`${API}/api/ftz/sites/${form.id}`, { method: 'DELETE' });
      setSelectedId(0);
      setIsCreatingNew(false);
      setIsEditing(false);
      setForm({ ...emptyForm });
      await loadAll();
      setStatus('FTZ site deactivated');
    } catch (error) {
      setStatus(error.message || 'Failed to deactivate FTZ site');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ftz-sites-page">
      <div className="ftz-sites-shell">
        <aside className="ftz-sites-list">
          <div className="ftz-sites-toolbar">
            <h2><FaWarehouse /> FTZ Sites</h2>
            <div className="ftz-sites-toolbar-actions">
              <button type="button" className="btn" onClick={loadAll} disabled={loading}>
                <FaSync /> Refresh
              </button>
              <button type="button" className="btn primary" onClick={startNew}>
                <FaPlus /> New Site
              </button>
            </div>
          </div>

          <label className="ftz-sites-toggle">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            <span>Show inactive sites</span>
          </label>

          <div className="ftz-sites-list-scroll">
            {sites.length ? (
              sites.map((site) => (
                <button
                  key={site.id}
                  type="button"
                  className={`ftz-sites-list-item ${selectedId === site.id ? 'active' : ''}`}
                  onClick={() => selectSite(site)}
                >
                  <div className="ftz-sites-list-title">
                    Zone {site.zone_number}{site.site_number ? ` / Site ${site.site_number}` : ''}
                  </div>
                  <div className="ftz-sites-list-meta">
                    {warehouseNameById.get(Number(site.warehouseid)) || 'Unknown warehouse'}
                  </div>
                  <div className="ftz-sites-list-meta">
                    Port {site.port_code}{site.active ? '' : ' / Inactive'}
                  </div>
                </button>
              ))
            ) : (
              <div className="ftz-sites-empty">
                {loading ? 'Loading FTZ sites…' : 'No FTZ sites configured yet.'}
              </div>
            )}
          </div>
        </aside>

        <section className="ftz-sites-editor">
          {!selectedId && !isCreatingNew ? (
            <div className="ftz-sites-card ftz-sites-card-empty">
              <h3>Select an FTZ Site</h3>
              <p>Choose an existing FTZ site from the list or click `New Site` to create one.</p>
            </div>
          ) : (
          <div className="ftz-sites-card" ref={editorRef}>
            <div className="ftz-sites-card-head">
              <div>
                <h3>{isCreatingNew ? 'New FTZ Site' : (isEditing ? 'Edit FTZ Site' : 'FTZ Site Details')}</h3>
                <p>Configure the warehouse-to-zone/site mapping used by FTZ admissions.</p>
              </div>
              <div className="ftz-sites-card-actions">
                {form.id && isEditing ? (
                  <button type="button" className="btn danger" onClick={deactivateSite} disabled={saving}>
                    <FaTrash /> Deactivate
                  </button>
                ) : null}
                {isCreatingNew ? (
                  <button type="button" className="btn" onClick={cancelNew} disabled={saving}>
                    <FaTimes /> Cancel
                  </button>
                ) : null}
                {form.id && isEditing ? (
                  <button type="button" className="btn" onClick={cancelEdit} disabled={saving}>
                    <FaTimes /> Cancel
                  </button>
                ) : null}
                {form.id && !isEditing ? (
                  <button type="button" className="btn primary" onClick={startEdit}>
                    <FaEdit /> Edit
                  </button>
                ) : null}
                {(isCreatingNew || isEditing) ? (
                  <button type="button" className="btn primary" onClick={saveSite} disabled={saving}>
                    <FaRegSave /> {saving ? 'Saving…' : 'Save Site'}
                  </button>
                ) : null}
              </div>
            </div>

            {status ? <div className="ftz-sites-status">{status}</div> : null}

            <div className="ftz-sites-form-grid">
              <label>
                Warehouse
                <select
                  ref={warehouseSelectRef}
                  value={form.warehouseid}
                  onChange={(e) => setForm((current) => ({ ...current, warehouseid: e.target.value === '' ? '' : Number(e.target.value) }))}
                  disabled={!isCreatingNew && !isEditing}
                >
                  <option value="">Select warehouse</option>
                  {warehouses.map((warehouse) => (
                    <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
                  ))}
                </select>
              </label>

              <label>
                Zone Number
                <input
                  ref={zoneInputRef}
                  value={form.zone_number}
                  onChange={(e) => setForm((current) => ({ ...current, zone_number: e.target.value }))}
                  placeholder="e.g. 99"
                  disabled={!isCreatingNew && !isEditing}
                />
              </label>

              <label>
                Site Number
                <input
                  value={form.site_number}
                  onChange={(e) => setForm((current) => ({ ...current, site_number: e.target.value }))}
                  placeholder="Optional"
                  disabled={!isCreatingNew && !isEditing}
                />
              </label>

              <label>
                Port Code
                <input
                  value={form.port_code}
                  onChange={(e) => setForm((current) => ({ ...current, port_code: e.target.value }))}
                  placeholder="e.g. 2704"
                  disabled={!isCreatingNew && !isEditing}
                />
              </label>

              <label>
                FIRMS Code
                <input
                  value={form.firms_code}
                  onChange={(e) => setForm((current) => ({ ...current, firms_code: e.target.value }))}
                  disabled={!isCreatingNew && !isEditing}
                />
              </label>

              <label>
                Operator Number
                <input
                  value={form.operator_number}
                  onChange={(e) => setForm((current) => ({ ...current, operator_number: e.target.value }))}
                  disabled={!isCreatingNew && !isEditing}
                />
              </label>

              <label className="span-2">
                Operator Name
                <input
                  value={form.operator_name}
                  onChange={(e) => setForm((current) => ({ ...current, operator_name: e.target.value }))}
                  disabled={!isCreatingNew && !isEditing}
                />
              </label>

              <label className="ftz-sites-checkbox">
                <input
                  type="checkbox"
                  checked={Number(form.direct_delivery_enabled || 0) === 1}
                  onChange={(e) => setForm((current) => ({ ...current, direct_delivery_enabled: e.target.checked ? 1 : 0 }))}
                  disabled={!isCreatingNew && !isEditing}
                />
                <span>Direct delivery enabled</span>
              </label>

              <label className="ftz-sites-checkbox">
                <input
                  type="checkbox"
                  checked={Number(form.active || 0) === 1}
                  onChange={(e) => setForm((current) => ({ ...current, active: e.target.checked ? 1 : 0 }))}
                  disabled={!isCreatingNew && !isEditing}
                />
                <span>Active</span>
              </label>
            </div>
          </div>
          )}
        </section>
      </div>
    </div>
  );
}
